import React from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, DialogActions, DialogContent, Grid, IconButton, Paper, Typography } from '@mui/material'
import LockOpen from '@mui/icons-material/LockOpen'
import Lock from '@mui/icons-material/Lock'
import { Loader } from '@carsys/ui/src/Loader'
import { Dialog, DialogTitleWithCloseButton } from '@carsys/ui'
import { AccountancySoftwareListItem } from '@cbgms/api'
import { formatEnum } from '@cbgms/base/utils/helpers'
import { useQuery } from '@cbgms/base/http/use-query'
import { useNotifier } from '@cbgms/base/notifier'
import { loadAccountancySoftware } from '../state/actions'
import Toggle from './Toggle'
import { IRootReducerState } from 'state/reducers'
import { accountancySoftwareRoute } from 'routes/routeNames'

export interface AdministrationData {
  keyval: string
  value: string
  name: string
  isactive: boolean
}

const AccountancySoftwareList: React.FC = () => {
  const dragID = 'drag-confirmation-dialog'
  const [modalOpen, setModalOpen] = React.useState(false)
  const [disabled, setDisabled] = React.useState(true)

  const notifier = useNotifier()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const enums = useSelector((state: IRootReducerState) => state.enums.EnumAccountancySoftware)

  const enumOptions = enums.map((admin: string) => ({
    key: admin,
    text: formatEnum('EnumAccountancySoftware', admin),
    value: admin
  }))

  // bring data from table
  const administrations = useQuery(['AccountancySoftwareList', 'loadAccountancySoftware'], async () => {
    const { Data } = await dispatch(loadAccountancySoftware({ fields: '*' }))

    // filter all data that are present in enum
    const admin: AdministrationData[] = Data.filter((administration: AccountancySoftwareListItem) =>
      enums.includes(administration.AccountancySoftware.Value)
    ).map(({ AccountancySoftware }) => {
      return {
        keyval: AccountancySoftware.UUID,
        value: AccountancySoftware.Value,
        name: AccountancySoftware.Name,
        isactive: AccountancySoftware.IsActive
      }
    })
    return {
      admin
    }
  })

  if (administrations.isError) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to={accountancySoftwareRoute} />
  }

  if (administrations.isLoading || !administrations.data) {
    return <Loader loading />
  }

  const softwareOptions: AdministrationData[] = enumOptions.map(enumAdmin => {
    return {
      keyval: administrations.data.admin.find(x => x.value === enumAdmin.key)?.keyval ?? '',
      value: enumAdmin.key,
      name: enumAdmin.text,
      isactive: administrations.data.admin.find(x => x.value === enumAdmin.key)?.isactive ?? false
    }
  })

  const toggleData = softwareOptions.map((admin: AdministrationData) => (
    <Grid item xs={2} key={admin.value}>
      <Toggle {...admin} disabled={disabled} />
    </Grid>
  ))

  const handleModalOpen = () => {
    setModalOpen(true)
  }
  const handleModalClose = () => {
    setModalOpen(false)
  }
  const EnableServices = () => {
    setModalOpen(!modalOpen)
    setDisabled(!disabled)
  }

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Grid container direction='row' spacing={2}>
          <Grid item xs={11}>
            <Typography variant='h3'>{t('Administration.AdministrationFilter')}</Typography>
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={() => handleModalOpen()} size='large'>
              {!disabled ? <LockOpen /> : <Lock />}
            </IconButton>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container direction='row'>
            {toggleData}
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={modalOpen} onClose={handleModalClose}>
        <DialogTitleWithCloseButton onClose={handleModalClose} draggableIDHandler={dragID}>
          {t('Confirmation.AdminFilterConfirm')}
        </DialogTitleWithCloseButton>
        <DialogContent>{t('Confirmation.AdminFilterConfirmText')}</DialogContent>
        <DialogActions>
          <Button color='primary' variant='contained' onClick={EnableServices}>
            {t('Confirmation.ConfirmAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AccountancySoftwareList
